import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Empty, Badge, Modal, Select, Input } from "@/components/ui-kit";
import { CreditCard, CheckCircle2, BellRing, FileText, Sheet, Download, History, Wallet, Mail, Settings, Gift, Pencil, Trash2, CalendarPlus, PiggyBank, ChevronUp, ChevronDown, Search, TrendingUp, TrendingDown, Plus, Lock, CalendarClock, Clock, Archive, ArchiveRestore, List, LayoutGrid } from "lucide-react";
import { fmtDate as fmtDateUtil } from "@/lib/utils";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const currentSeason = () => {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 7 ? y : y - 1;
  return `${start}-${start + 1}`;
};
const seasonOptions = () => {
  const cur = parseInt(currentSeason().split("-")[0]);
  const arr = [];
  for (let s = cur + 1; s >= cur - 3; s--) arr.push(`${s}-${s + 1}`);
  return arr;
};

const PLAN_STATUS = {
  payee: { label: "Payée", cls: "bg-primary/15 text-primary" },
  a_venir: { label: "À venir", cls: "bg-secondary text-muted-foreground" },
  en_retard: { label: "En retard", cls: "bg-destructive/15 text-destructive" },
};
const fmtEch = (iso) => { try { return fmtDateUtil(iso); } catch { return iso; } };

export default function Cotisations() {
  const { user } = useAuth();
  const canRemind = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [season, setSeason] = useState(currentSeason());
  const [statusFilter, setStatusFilter] = useState("active");
  const [payFilter, setPayFilter] = useState("all");
  const [mtypeFilter, setMtypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editName, setEditName] = useState(null); // {member, first_name, last_name}
  const [savingName, setSavingName] = useState(false);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ members: [], fee_types: [] });
  const [loadingKey, setLoadingKey] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [histMember, setHistMember] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [acompte, setAcompte] = useState(null); // {member, fee}
  const [acompteAmount, setAcompteAmount] = useState("");
  const [manual, setManual] = useState(null); // {member, fee}
  const [manualAmount, setManualAmount] = useState("");
  const [manualMethod, setManualMethod] = useState("cash");
  const [manualRef, setManualRef] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sessionFeeInput, setSessionFeeInput] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [session, setSession] = useState(null); // {member}
  const [sessionAmount, setSessionAmount] = useState("");
  const [sessionMethod, setSessionMethod] = useState("cash");
  const [sessionLoading, setSessionLoading] = useState(false);
  const [paidEvents, setPaidEvents] = useState([]);
  const [addEventFor, setAddEventFor] = useState(null); // member
  const [treasury, setTreasury] = useState(null);
  const [finView, setFinView] = useState("list");
  const [treasuryOpen, setTreasuryOpen] = useState(false);
  const [treasuryDetail, setTreasuryDetail] = useState(null); // event row
  const [tab, setTab] = useState("cotisations"); // cotisations | tresorerie
  const [paymentLink, setPaymentLink] = useState("");
  const [paymentLinkInput, setPaymentLinkInput] = useState("");
  const [planCtx, setPlanCtx] = useState(null); // {member, fee}
  const [planRows, setPlanRows] = useState([]); // [{amount, due_date}]
  const [planSaving, setPlanSaving] = useState(false);

  const sendReceipt = async (txnId) => {
    if (!txnId) { toast.error("Reçu indisponible"); return; }
    setSendingReceipt(txnId);
    try {
      const { data } = await api.post(`/payments/receipt/${txnId}/send`);
      toast.success(`Reçu envoyé par email (${data.recipients.join(", ")})`);
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); }
    finally { setSendingReceipt(null); }
  };

  const load = () => api.get(`/fees?season=${season}&status=${statusFilter}`).then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [season, statusFilter]);
  useEffect(() => {
    const mid = searchParams.get("member");
    if (mid && data.members.length) {
      const m = data.members.find((x) => x.member_id === mid);
      if (m) { setSearch(`${m.first_name} ${m.last_name}`); openHistory(m); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.members]);
  useEffect(() => {
    if (isAdmin) api.get("/paid-events").then((r) => setPaidEvents(r.data)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) api.get(`/events/treasury?season=${season}`).then((r) => setTreasury(r.data)).catch(() => {});
  }, [isAdmin, season, data]);
  useEffect(() => {
    api.get("/settings/payment-link").then((r) => setPaymentLink(r.data.url || "")).catch(() => {});
  }, []);

  const enrollEvent = async (m, ev) => {
    try {
      await api.post(`/events/${ev.id}/camp/participation`, { member_id: m.member_id, status: "in" });
      toast.success(`${ev.title} ajouté à ${m.first_name}`);
      setAddEventFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeEvent = async (m, fee) => {
    if (!window.confirm(`Retirer « ${fee.label} » pour ${m.first_name} ${m.last_name} ?`)) return;
    try {
      await api.post(`/events/${fee.event_id}/camp/participation`, { member_id: m.member_id, status: "out" });
      toast.success("Retiré"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openHistory = async (m) => {
    setHistMember(m); setHistory([]); setHistLoading(true);
    try { const { data } = await api.get(`/payments/history/${m.member_id}`); setHistory(data); }
    catch { toast.error("Impossible de charger l'historique"); }
    finally { setHistLoading(false); }
  };
  const fmtDate = (iso) => iso ? (String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/) ? (m => `${m[3]}/${m[2]}/${m[1].slice(-2)}`)(String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/)) : new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })) : "—";

  const sendReminders = async () => {
    setReminding(true);
    try { const { data } = await api.post("/reminders/cotisations"); toast.success(`${data.sent} rappel(s) envoyé(s)`); }
    catch { toast.error("Erreur lors de l'envoi des rappels"); }
    finally { setReminding(false); }
  };

  const pay = async (m, fee, amount) => {
    const key = `${m.member_id}-${fee.key}`;
    setLoadingKey(key);
    try {
      const payload = { member_id: m.member_id, fee_type: fee.key, season, origin_url: window.location.origin };
      if (amount) payload.amount = amount;
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Impossible de démarrer le paiement"); setLoadingKey(null); }
  };

  const submitAcompte = (e) => {
    e.preventDefault();
    const amt = parseFloat(acompteAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    if (amt > acompte.fee.remaining) { toast.error(`Maximum ${acompte.fee.remaining} €`); return; }
    const { member, fee } = acompte;
    setAcompte(null); setAcompteAmount("");
    pay(member, fee, amt);
  };

  const submitManual = async (e) => {
    e.preventDefault();
    const amt = parseFloat(manualAmount);
    const maxAmt = manual.fee.remaining ?? manual.fee.amount;
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    if (amt > maxAmt + 0.001) { toast.error(`Maximum ${maxAmt} €`); return; }
    setManualLoading(true);
    try {
      const { data } = await api.post("/payments/manual", {
        member_id: manual.member.member_id, fee_type: manual.fee.key, season,
        amount: amt, method: manualMethod, reference: manualRef,
      });
      toast.success(data.fully_paid ? "Cotisation en ordre de paiement ✓" : `Encaissé ${data.amount} € · reste ${data.remaining} €`);
      setManual(null); setManualAmount(""); setManualRef("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Enregistrement impossible"); }
    finally { setManualLoading(false); }
  };

  const setOverride = async (m, fee) => {
    const cur = fee.custom_amount ? fee.amount : "";
    const v = window.prompt(`Montant dû pour ${m.first_name} ${m.last_name} — ${fee.label}\nTarif normal : ${fee.base_amount ?? fee.amount} €\n(laisser vide pour réinitialiser au tarif normal)`, cur);
    if (v === null) return;
    try { await api.put("/fees/override", { member_id: m.member_id, fee_type: fee.key, season, amount: v.trim() === "" ? null : parseFloat(v) }); toast.success("Montant mis à jour"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openPlan = (m, fee) => {
    const existing = (fee.plan?.installments || []).map((i) => ({ amount: String(i.amount), due_date: i.due_date }));
    setPlanRows(existing.length ? existing : []);
    setPlanCtx({ member: m, fee });
  };
  const addPlanRow = () => setPlanRows((r) => [...r, { amount: "", due_date: "" }]);
  const setPlanRow = (i, field, val) => setPlanRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  const removePlanRow = (i) => setPlanRows((r) => r.filter((_, idx) => idx !== i));
  const autoPlan = (n) => {
    if (!planCtx) return;
    const total = planCtx.fee.amount || 0;
    const per = Math.floor((total / n) * 100) / 100;
    const rows = [];
    const base = new Date();
    for (let k = 0; k < n; k++) {
      const d = new Date(base.getFullYear(), base.getMonth() + k, base.getDate());
      const amt = k === n - 1 ? Math.round((total - per * (n - 1)) * 100) / 100 : per;
      rows.push({ amount: String(amt), due_date: d.toISOString().slice(0, 10) });
    }
    setPlanRows(rows);
  };
  const savePlan = async () => {
    if (!planCtx) return;
    setPlanSaving(true);
    try {
      const installments = planRows
        .filter((r) => r.amount !== "" && r.due_date)
        .map((r) => ({ amount: parseFloat(r.amount), due_date: r.due_date }));
      await api.put("/fees/plan", { member_id: planCtx.member.member_id, fee_type: planCtx.fee.key, season, installments });
      toast.success(installments.length ? "Plan de paiement enregistré" : "Plan supprimé");
      setPlanCtx(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPlanSaving(false); }
  };
  const editTxn = async (t) => {
    const amt = window.prompt("Corriger le montant (€) :", t.amount);
    if (amt === null) return;
    const method = window.prompt("Mode (cash / bancontact / lien / carte) :", t.method || "cash");
    if (method === null) return;
    const ref = window.prompt("Référence (facultatif) :", t.reference || "");
    if (ref === null) return;
    try { await api.put(`/payments/transaction/${t.id}`, { amount: parseFloat(amt), method: method.trim().toLowerCase(), reference: ref }); toast.success("Paiement corrigé"); if (histMember) openHistory(histMember); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delTxn = async (t) => {
    if (!window.confirm("Supprimer définitivement ce paiement ?")) return;
    try { await api.delete(`/payments/transaction/${t.id}`); toast.success("Paiement supprimé"); if (histMember) openHistory(histMember); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const updateTxnDate = async (t, dateStr) => {
    try { await api.put(`/payments/transaction/${t.id}`, { paid_at: dateStr }); toast.success("Date « Payé le » mise à jour"); if (histMember) openHistory(histMember); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    const amt = parseFloat(sessionFeeInput);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    setSavingSettings(true);
    try {
      await api.put("/settings/session-fee", { amount: amt });
      await api.put("/settings/payment-link", { url: paymentLinkInput.trim() });
      setPaymentLink(paymentLinkInput.trim());
      toast.success("Réglages mis à jour");
      setSettingsOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSavingSettings(false); }
  };

  const convertToAffilie = async (m) => {
    if (!window.confirm(`Convertir ${m.first_name} ${m.last_name} en Affilié ?\nLes ${m.session_count || 0} séance(s) déjà payée(s) (${m.session_total || 0} €) seront déduites du montant d'affiliation.`)) return;
    try {
      const { data } = await api.post(`/members/${m.member_id}/convert-to-affilie`, { season });
      toast.success(data.credit > 0
        ? `Converti en Affilié · ${data.credit} € déduits (${data.session_count} séance(s))`
        : "Converti en Affilié");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Conversion impossible"); }
  };

  const submitSession = async (e) => {
    e.preventDefault();
    const amt = parseFloat(sessionAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    setSessionLoading(true);
    try {
      const { data } = await api.post("/payments/session", {
        member_id: session.member.member_id, season, amount: amt, method: sessionMethod,
      });
      toast.success(`Séance encaissée : ${data.amount} € · ${data.session_count} séance(s) au total`);
      setSession(null); setSessionAmount("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSessionLoading(false); }
  };

  const saveName = async (e) => {
    e.preventDefault();
    const fn = editName.first_name.trim(), ln = editName.last_name.trim();
    if (!fn || !ln) { toast.error("Prénom et nom obligatoires"); return; }
    setSavingName(true);
    try {
      await api.patch(`/members/${editName.member.member_id}/status`, { first_name: fn, last_name: ln });
      toast.success("Nom mis à jour"); setEditName(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingName(false); }
  };
  const archiveMember = async (m, archived) => {
    if (!window.confirm(archived
      ? `Archiver ${m.first_name} ${m.last_name} ? Il sera retiré des listes actives (données conservées).`
      : `Réactiver ${m.first_name} ${m.last_name} ?`)) return;
    try {
      await api.patch(`/members/${m.member_id}/status`, { archived });
      toast.success(archived ? "Affilié archivé" : "Affilié réactivé"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const memberEnOrdre = (m) => (m.membership_type || "affilie") === "affilie" && (m.fees || []).length > 0 && m.fees.every((f) => f.paid);
  const exportQuery = () => `season=${season}&status=${statusFilter}&q=${encodeURIComponent(search)}&pay=${payFilter}&mtype=${mtypeFilter}`;

  const allFees = data.members.flatMap((m) => [...(m.fees || []), ...(m.events || [])]);
  const paidCount = allFees.filter((f) => f.paid).length;
  const unpaidCount = allFees.filter((f) => !f.paid).length;
  const sessionCollected = data.members.reduce((a, m) => a + (m.session_total || 0), 0);
  const collected = allFees.filter((f) => f.paid).reduce((a, f) => a + f.amount, 0) + sessionCollected;
  const pending = allFees.filter((f) => !f.paid).reduce((a, f) => a + f.amount, 0);
  const pieData = [{ name: "Payées", value: paidCount }, { name: "En attente", value: unpaidCount }];

  const feeRow = (m, fee, isEvent = false) => (
    <div key={fee.key} data-testid={`fee-row-${m.member_id}-${fee.key}`}
      className="rounded-lg border border-border bg-secondary/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate flex items-center gap-1.5">{fee.label}
            {fee.custom_amount && <Badge color="yellow" data-testid={`custom-amount-${m.member_id}-${fee.key}`}>Montant ajusté</Badge>}
            {isAdmin && (
              <button data-testid={`edit-amount-${m.member_id}-${fee.key}`} onClick={() => setOverride(m, fee)}
                className="p-1 text-muted-foreground hover:text-primary" title="Modifier le montant dû pour cet enfant"><Pencil size={13} /></button>
            )}
            {isAdmin && !fee.paid && (
              <button data-testid={`plan-btn-${m.member_id}-${fee.key}`} onClick={() => openPlan(m, fee)}
                className={`p-1 hover:text-primary ${fee.plan ? "text-primary" : "text-muted-foreground"}`} title="Plan de paiement (échéancier)"><CalendarClock size={13} /></button>
            )}
            {isEvent && isAdmin && (
              <button data-testid={`remove-event-${m.member_id}-${fee.event_id}`} onClick={() => removeEvent(m, fee)}
                className="p-1 text-muted-foreground hover:text-destructive" title="Retirer cet événement pour cet enfant"><Trash2 size={13} /></button>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {fee.paid ? `${fee.amount} €` :
              fee.paid_amount > 0 ? `Payé ${fee.paid_amount} € / ${fee.amount} € · reste ${fee.remaining} €` : `${fee.amount} €`}
            {isEvent && fee.deposit > 0 ? ` · acompte ${fee.deposit} €` : ""}
          </p>
        </div>
        {fee.paid ? (
          <div className="flex items-center gap-2 shrink-0">
            <Badge color="green"><span className="flex items-center gap-1"><CheckCircle2 size={12} /> Payée</span></Badge>
            <button data-testid={`receipt-btn-${m.member_id}-${fee.key}`}
              onClick={() => downloadFile(`/payments/receipt/${m.member_id}.pdf${fee.transaction_id ? `?txn=${fee.transaction_id}` : ""}`, `recu_${m.first_name}_${m.last_name}_${fee.key}.pdf`).catch(() => toast.error("Reçu indisponible"))}
              className="p-2 text-muted-foreground hover:text-primary" title="Télécharger le reçu"><Download size={16} /></button>
            {isAdmin && fee.transaction_id && (
              <button data-testid={`send-receipt-btn-${m.member_id}-${fee.key}`}
                onClick={() => sendReceipt(fee.transaction_id)} disabled={sendingReceipt === fee.transaction_id}
                className="p-2 text-muted-foreground hover:text-primary disabled:opacity-50" title="Envoyer le reçu par email"><Mail size={16} /></button>
            )}
          </div>
        ) : isAdmin ? (
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid={`encaisser-btn-${m.member_id}-${fee.key}`}
              onClick={() => { setManual({ member: m, fee }); setManualAmount(String(fee.remaining ?? fee.amount)); setManualMethod("cash"); setManualRef(""); }}>
              <Wallet size={15} /> Encaisser
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid={`acompte-btn-${m.member_id}-${fee.key}`} variant="outline"
              onClick={() => { setAcompte({ member: m, fee }); setAcompteAmount(""); }}>Acompte</Button>
            <Button data-testid={`pay-btn-${m.member_id}-${fee.key}`} onClick={() => pay(m, fee)} disabled={loadingKey === `${m.member_id}-${fee.key}`}>
              <CreditCard size={15} /> {loadingKey === `${m.member_id}-${fee.key}` ? "..." : (fee.paid_amount > 0 ? "Solde" : "Payer")}
            </Button>
          </div>
        )}
      </div>
      {!fee.paid && fee.paid_amount > 0 && (
        <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (fee.paid_amount / fee.amount) * 100)}%` }} />
        </div>
      )}
      {fee.plan?.installments?.length > 0 && (
        <div data-testid={`plan-view-${m.member_id}-${fee.key}`} className="mt-3 rounded-lg bg-secondary/40 p-2.5 space-y-1.5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><CalendarClock size={12} /> Plan de paiement</p>
          {fee.plan.installments.map((it, i) => { const M = PLAN_STATUS[it.status] || PLAN_STATUS.a_venir; return (
            <div key={i} data-testid={`installment-${m.member_id}-${fee.key}-${i}`} className="flex items-center justify-between gap-2 text-xs">
              <span className="text-muted-foreground">Échéance {i + 1} · <b className="text-foreground">{fmtEch(it.due_date)}</b></span>
              <span className="flex items-center gap-2">
                <span className="font-semibold">{it.amount} €</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${M.cls}`}>{M.label}</span>
              </span>
            </div>); })}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="Financier" subtitle={`Saison ${season} — cotisations, stages & événements payants.`}
        action={(
          <div className="flex gap-2 flex-wrap">
            {paymentLink && tab === "cotisations" && (
              <Button data-testid="pay-online-btn" onClick={() => window.open(paymentLink, "_blank", "noopener")}>
                <CreditCard size={16} /> Payer en ligne
              </Button>
            )}
            {canRemind && tab === "cotisations" && (<>
              <Button data-testid="session-fee-settings-btn" variant="outline"
                onClick={() => { setSessionFeeInput(String(data.session_fee ?? "")); setPaymentLinkInput(paymentLink); setSettingsOpen(true); }}>
                <Settings size={16} /> Réglages
              </Button>
              <Button data-testid="export-cotisations-pdf" variant="outline" onClick={() => downloadFile(`/export/cotisations.pdf?${exportQuery()}`, `cotisations_${season}.pdf`).catch(() => toast.error("Export échoué"))}><FileText size={16} /> PDF</Button>
              <Button data-testid="export-cotisations-xlsx" variant="outline" onClick={() => downloadFile(`/export/cotisations.xlsx?${exportQuery()}`, `cotisations_${season}.xlsx`).catch(() => toast.error("Export échoué"))}><Sheet size={16} /> Excel</Button>
              <Button data-testid="send-reminders-btn" variant="outline" onClick={sendReminders} disabled={reminding}><BellRing size={16} /> {reminding ? "Envoi..." : "Rappels"}</Button>
            </>)}
          </div>
        )} />

      {isAdmin && (
        <div className="flex gap-2 mb-6 border-b border-border">
          <button data-testid="tab-cotisations" onClick={() => setTab("cotisations")}
            className={`px-4 py-2.5 text-sm font-bold uppercase tracking-wide -mb-px border-b-2 transition-colors ${tab === "cotisations" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <span className="flex items-center gap-2"><CreditCard size={15} /> Cotisations & événements</span>
          </button>
          <button data-testid="tab-tresorerie" onClick={() => setTab("tresorerie")}
            className={`px-4 py-2.5 text-sm font-bold uppercase tracking-wide -mb-px border-b-2 transition-colors ${tab === "tresorerie" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <span className="flex items-center gap-2"><PiggyBank size={15} /> Trésorerie</span>
          </button>
        </div>
      )}

      {isAdmin && tab === "tresorerie" && <ClubTreasury />}

      {tab === "cotisations" && (<>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Saison</label>
        <div className="w-52">
          <Select data-testid="season-select" value={season} onChange={(e) => setSeason(e.target.value)}>
            {seasonOptions().map((s) => <option key={s} value={s}>Saison {s}</option>)}
          </Select>
        </div>
        {isAdmin && (
          <div className="w-56">
            <Select data-testid="fin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Tous (actifs)</option>
              <option value="season">Affiliés 2026/2027</option>
              <option value="archived">Archivés</option>
            </Select>
          </div>
        )}
        {isAdmin && (
          <div className="w-52">
            <Select data-testid="fin-pay-filter" value={payFilter} onChange={(e) => setPayFilter(e.target.value)}>
              <option value="all">Ordre de paiement : tous</option>
              <option value="paid">En ordre de paiement</option>
              <option value="unpaid">À régler</option>
            </Select>
          </div>
        )}
        {isAdmin && (
          <div className="w-48">
            <Select data-testid="fin-type-filter" value={mtypeFilter} onChange={(e) => setMtypeFilter(e.target.value)}>
              <option value="all">Type : tous</option>
              <option value="affilie">Affilié</option>
              <option value="essai">Essai</option>
              <option value="seance">À la séance</option>
              <option value="stages">Stages</option>
            </Select>
          </div>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
          <Input data-testid="fin-search" placeholder="Rechercher un affilié par nom..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Card><p className="text-xs uppercase tracking-widest text-muted-foreground">Encaissé</p><p className="font-display text-3xl font-black text-primary mt-2">{collected} €</p></Card>
        <Card><p className="text-xs uppercase tracking-widest text-muted-foreground">En attente</p><p className="font-display text-3xl font-black text-yellow-500 mt-2">{pending} €</p></Card>
        <Card>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Répartition</p>
          <ResponsiveContainer width="100%" height={90}>
            <PieChart>
              <Pie data={pieData} dataKey="value" innerRadius={25} outerRadius={40} paddingAngle={3}>
                <Cell fill="#22c55e" /><Cell fill="#facc15" />
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(240 5% 8%)", border: "1px solid hsl(240 5% 20%)", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {isAdmin && treasury && treasury.events.length > 0 && (
        <Card className="mb-8" data-testid="treasury-panel">
          <button data-testid="treasury-toggle" onClick={() => setTreasuryOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <PiggyBank size={18} className="text-primary" />
              <span className="font-semibold text-sm">Trésorerie — stages & événements payants</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground">
                Encaissé <b className="text-primary">{treasury.total_collected} €</b> / attendu <b className="text-foreground">{treasury.total_expected} €</b>
              </span>
              {treasuryOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>
          {treasuryOpen && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="treasury-events">
              {treasury.events.map((ev) => {
                const pct = ev.expected > 0 ? Math.min(100, (ev.collected / ev.expected) * 100) : 0;
                return (
                  <button key={ev.event_id} data-testid={`treasury-event-${ev.event_id}`}
                    onClick={() => setTreasuryDetail(ev)}
                    className="text-left rounded-lg border border-border bg-secondary/30 p-3 hover:border-primary transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold truncate">{ev.title}</p>
                      <Badge color={ev.remaining <= 0 ? "green" : "yellow"}>{ev.participants} inscrit(s)</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDate(ev.date)}{ev.end_date && ev.end_date !== ev.date ? ` → ${fmtDate(ev.end_date)}` : ""} · {ev.cost} € / enfant</p>
                    <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs mt-1"><span className="text-primary font-bold">{ev.collected} €</span> / {ev.expected} € · reste <b className="text-yellow-500">{ev.remaining} €</b></p>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {(() => { const shown = data.members.filter((m) => {
        if (search && !`${m.first_name} ${m.last_name}`.toLowerCase().includes(search.toLowerCase())) return false;
        if (mtypeFilter !== "all" && (m.membership_type || "affilie") !== mtypeFilter) return false;
        if (payFilter === "paid" && !memberEnOrdre(m)) return false;
        if (payFilter === "unpaid" && !((m.membership_type || "affilie") === "affilie" && !memberEnOrdre(m))) return false;
        return true;
      }); return shown.length ? (
        <>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <p className="text-xs text-muted-foreground">{shown.length} affilié(s)</p>
          <div className="inline-flex items-center gap-1 rounded-lg bg-secondary p-1" data-testid="fin-view-toggle">
            <button data-testid="fin-view-list" onClick={() => setFinView("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${finView === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><List size={14} /> Liste</button>
            <button data-testid="fin-view-table" onClick={() => setFinView("table")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${finView === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><LayoutGrid size={14} /> Tableau</button>
          </div>
        </div>
        {finView === "table" ? (
          <Card className="overflow-x-auto p-0" data-testid="fin-table">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="sticky left-0 bg-card px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground font-bold whitespace-nowrap">Nom Prénom</th>
                  <th className="px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-bold text-center">Année</th>
                  {(data.fee_types || []).map((ft) => (
                    <th key={ft.key} className="px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-bold text-center whitespace-nowrap">{ft.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => {
                  const mtype = m.membership_type || "affilie";
                  const feeByKey = Object.fromEntries((m.fees || []).map((f) => [f.key, f]));
                  const cell = (ft) => {
                    if (mtype === "essai") return <Badge color="blue">Essai</Badge>;
                    if (mtype === "seance") return <Badge color="yellow">Séance</Badge>;
                    const f = feeByKey[ft.key];
                    if (!f) return <span className="text-muted-foreground/40">—</span>;
                    if (f.paid) return <Badge color="green"><span className="flex items-center gap-1"><CheckCircle2 size={11} /> En ordre</span></Badge>;
                    if (f.paid_amount > 0) return <Badge color="yellow">Partiel · {f.paid_amount}/{f.amount}€</Badge>;
                    return <Badge color="red">À payer</Badge>;
                  };
                  return (
                    <tr key={m.member_id} data-testid={`fin-table-row-${m.member_id}`} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="sticky left-0 bg-card px-4 py-2.5 whitespace-nowrap">
                        <button data-testid={`fin-table-name-${m.member_id}`} onClick={() => openHistory(m)}
                          className="font-semibold text-left hover:text-primary hover:underline transition-colors">{m.last_name} {m.first_name}</button>
                      </td>
                      <td className="px-3 py-2.5 text-center text-muted-foreground">{m.category || "—"}</td>
                      {(data.fee_types || []).map((ft) => (
                        <td key={ft.key} className="px-3 py-2.5 text-center">{cell(ft)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        ) : (
        <div className="space-y-3">
          {shown.map((m) => (
            <Card key={m.member_id} data-testid="cotisation-row" className="py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-black">{m.first_name?.[0]}{m.last_name?.[0]}</div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`fin-name-${m.member_id}`} onClick={() => openHistory(m)}
                        className="font-semibold text-sm text-left hover:text-primary hover:underline transition-colors">{m.first_name} {m.last_name}</button>
                      {isAdmin && (
                        <button data-testid={`fin-edit-name-${m.member_id}`} title="Modifier nom / prénom"
                          onClick={() => setEditName({ member: m, first_name: m.first_name || "", last_name: m.last_name || "" })}
                          className="p-1 text-muted-foreground hover:text-primary"><Pencil size={13} /></button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{m.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {m.membership_type === "essai" && (
                    <Badge color="blue" data-testid={`essai-badge-${m.member_id}`}><span className="flex items-center gap-1"><Gift size={12} /> Essai · Gratuit</span></Badge>
                  )}
                  {m.membership_type === "seance" && (
                    <Badge color="yellow" data-testid={`seance-badge-${m.member_id}`}>À la séance</Badge>
                  )}
                  {m.membership_type === "affilie" && m.fees.length > 0 && m.fees.every((f) => f.paid) && (
                    <Badge color="green" data-testid={`en-ordre-${m.member_id}`}><span className="flex items-center gap-1"><CheckCircle2 size={12} /> En ordre de paiement</span></Badge>
                  )}
                  <Button data-testid={`history-btn-${m.member_id}`} variant="outline" onClick={() => openHistory(m)}>
                    <History size={15} /> Historique
                  </Button>
                  {isAdmin && (
                    <button data-testid={`fin-archive-${m.member_id}`}
                      onClick={() => archiveMember(m, !m.archived)}
                      className={`p-2 ${m.archived ? "text-primary hover:text-primary/80" : "text-muted-foreground hover:text-destructive"}`}
                      title={m.archived ? "Réactiver l'affilié" : "Archiver l'affilié"}>
                      {m.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                    </button>
                  )}
                </div>
              </div>
              {m.membership_type === "essai" ? (
                <p className="text-sm text-muted-foreground" data-testid={`essai-info-${m.member_id}`}>
                  Joueur en période d'essai — aucune cotisation à régler.
                </p>
              ) : m.membership_type === "seance" ? (
                <div className="rounded-lg border border-border bg-secondary/30 p-3 flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`seance-block-${m.member_id}`}>
                  <div>
                    <p className="text-sm font-semibold">{m.session_count || 0} séance(s) payée(s)</p>
                    <p className="text-xs text-muted-foreground">Total encaissé : {m.session_total || 0} € · Tarif : {data.session_fee} € / séance</p>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Button data-testid={`convertir-affilie-btn-${m.member_id}`} variant="outline"
                        onClick={() => convertToAffilie(m)}>
                        <CheckCircle2 size={15} /> Convertir en affilié
                      </Button>
                      <Button data-testid={`encaisser-seance-btn-${m.member_id}`}
                        onClick={() => { setSession({ member: m }); setSessionAmount(String(data.session_fee ?? "")); setSessionMethod("cash"); }}>
                        <Wallet size={15} /> Encaisser une séance
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {m.fees.map((fee) => feeRow(m, fee))}
              </div>
              )}

              {/* Stages & événements payants (tous types de membres) */}
              {((m.events && m.events.length) || isAdmin) && (
                <div className="mt-3 pt-3 border-t border-border/60" data-testid={`events-section-${m.member_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Stages & événements payants</p>
                    {isAdmin && (
                      <Button data-testid={`add-event-btn-${m.member_id}`} variant="outline"
                        onClick={() => setAddEventFor(m)}>
                        <CalendarPlus size={15} /> Ajouter
                      </Button>
                    )}
                  </div>
                  {m.events && m.events.length ? (
                    <div className="grid sm:grid-cols-2 gap-2">
                      {m.events.map((fee) => feeRow(m, fee, true))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid={`no-events-${m.member_id}`}>Aucun stage / événement payant.</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
        )}
        </>
      ) : <Empty text={search ? "Aucun affilié ne correspond à la recherche." : "Aucun membre à facturer."} />; })()}
      </>)}

      <Modal open={!!histMember} onClose={() => setHistMember(null)}
        title={histMember ? `Historique — ${histMember.first_name} ${histMember.last_name}` : "Historique"}>
        {histLoading ? (
          <p className="text-sm text-muted-foreground">Chargement...</p>
        ) : history.length ? (
          <div className="space-y-2" data-testid="payment-history-list">
            {history.map((t) => (
              <div key={t.id} data-testid="payment-history-row"
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                <div>
                  <p className="font-semibold text-sm">{t.fee_label} · {Number(t.amount).toFixed(2)} €</p>
                  <p className="text-xs text-muted-foreground">Saison {t.season} · Payé le {fmtDate(t.paid_at || t.updated_at || t.created_at)}{t.method ? ` · ${({ lien: "Lien", cash: "Espèces", carte: "Carte", bancontact: "Bancontact", avoir: "Avoir (séances)" }[t.method] || t.method)}` : ""}{t.reference ? ` · Réf : ${t.reference}` : ""}</p>
                  {isAdmin && t.payment_status === "paid" && (
                    <label className="text-xs text-muted-foreground flex items-center gap-2 mt-1.5">
                      Payé le
                      <input type="date" data-testid={`history-paid-date-${t.id}`}
                        value={(t.paid_at || t.updated_at || t.created_at || "").slice(0, 10)}
                        onChange={(e) => e.target.value && updateTxnDate(t, e.target.value)}
                        className="h-8 px-2 rounded-md bg-secondary border border-border text-foreground text-xs outline-none focus:border-primary" />
                    </label>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge color={t.payment_status === "paid" ? "green" : "yellow"}>
                    {t.payment_status === "paid" ? "Payé" : "En attente"}
                  </Badge>
                  {t.payment_status === "paid" && (
                    <>
                      <Button data-testid={`history-receipt-${t.id}`} variant="outline"
                        onClick={() => downloadFile(`/payments/receipt/${histMember.member_id}.pdf?txn=${t.id}`, `recu_${histMember.first_name}_${histMember.last_name}_${t.season}.pdf`).catch(() => toast.error("Reçu indisponible"))}>
                        <Download size={14} /> Reçu
                      </Button>
                      {isAdmin && (
                        <Button data-testid={`history-send-${t.id}`} variant="outline"
                          onClick={() => sendReceipt(t.id)} disabled={sendingReceipt === t.id}>
                          <Mail size={14} /> {sendingReceipt === t.id ? "Envoi..." : "Envoyer"}
                        </Button>
                      )}
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <button data-testid={`history-edit-${t.id}`} onClick={() => editTxn(t)} title="Corriger ce paiement"
                        className="p-2 text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
                      <button data-testid={`history-delete-${t.id}`} onClick={() => delTxn(t)} title="Supprimer ce paiement"
                        className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <Empty text="Aucun paiement enregistré pour ce membre." />}
      </Modal>

      <Modal open={!!acompte} onClose={() => setAcompte(null)}
        title={acompte ? `Acompte — ${acompte.fee.label}` : "Acompte"}>
        {acompte && (
          <form onSubmit={submitAcompte}>
            <p className="text-sm text-muted-foreground mb-4">
              {acompte.member.first_name} {acompte.member.last_name} · Reste à régler : <b className="text-foreground">{acompte.fee.remaining} €</b>
            </p>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Montant de l'acompte (€)</label>
            <input data-testid="acompte-amount-input" type="number" step="0.01" min="1" max={acompte.fee.remaining}
              value={acompteAmount} onChange={(e) => setAcompteAmount(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
              placeholder={`Ex. ${Math.ceil(acompte.fee.remaining / 2)}`} autoFocus />
            <Button data-testid="acompte-submit-btn" className="w-full mt-4"><CreditCard size={15} /> Payer l'acompte</Button>
          </form>
        )}
      </Modal>
      <Modal open={!!planCtx} onClose={() => setPlanCtx(null)}
        title={planCtx ? `Plan de paiement — ${planCtx.fee.label}` : "Plan de paiement"}>
        {planCtx && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {planCtx.member.first_name} {planCtx.member.last_name} · Montant dû : <b className="text-foreground">{planCtx.fee.amount} €</b>
              {planCtx.fee.paid_amount > 0 ? <> · déjà payé {planCtx.fee.paid_amount} €</> : null}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Générer :</span>
              {[2, 3, 4].map((n) => (
                <button key={n} type="button" data-testid={`plan-auto-${n}`} onClick={() => autoPlan(n)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold bg-secondary text-muted-foreground hover:text-foreground">{n} versements</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
              {planRows.length === 0 && <p className="text-xs text-muted-foreground">Aucune échéance. Ajoutez-en une ou générez automatiquement.</p>}
              {planRows.map((row, i) => (
                <div key={i} data-testid={`plan-row-${i}`} className="flex items-center gap-2">
                  <input type="date" data-testid={`plan-date-${i}`} value={row.due_date} onChange={(e) => setPlanRow(i, "due_date", e.target.value)}
                    className="flex-1 rounded-lg bg-secondary border border-border p-2.5 text-sm outline-none focus:border-primary" />
                  <input type="number" step="0.01" min="0" data-testid={`plan-amount-${i}`} value={row.amount} onChange={(e) => setPlanRow(i, "amount", e.target.value)}
                    placeholder="Montant €" className="w-28 rounded-lg bg-secondary border border-border p-2.5 text-sm outline-none focus:border-primary" />
                  <button type="button" data-testid={`plan-remove-${i}`} onClick={() => removePlanRow(i)}
                    className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <button type="button" data-testid="plan-add-row" onClick={addPlanRow}
              className="text-xs font-bold text-primary flex items-center gap-1"><Plus size={13} /> Ajouter une échéance</button>
            {planRows.length > 0 && (() => {
              const sum = planRows.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);
              const diff = Math.round((sum - (planCtx.fee.amount || 0)) * 100) / 100;
              return <p className={`text-xs ${Math.abs(diff) < 0.01 ? "text-primary" : "text-yellow-500"}`}>
                Total des échéances : {Math.round(sum * 100) / 100} € {Math.abs(diff) < 0.01 ? "· correspond au montant dû ✓" : `· ${diff > 0 ? "+" : ""}${diff} € vs montant dû`}
              </p>;
            })()}
            <Button data-testid="plan-save-btn" className="w-full" onClick={savePlan} disabled={planSaving}>
              {planSaving ? "Enregistrement..." : (planRows.length ? "Enregistrer le plan" : "Supprimer le plan")}
            </Button>
          </div>
        )}
      </Modal>
      <Modal open={!!manual} onClose={() => setManual(null)}
        title={manual ? `Encaisser — ${manual.fee.label}` : "Encaisser"}>
        {manual && (
          <form onSubmit={submitManual}>
            <p className="text-sm text-muted-foreground mb-4">
              {manual.member.first_name} {manual.member.last_name} · Reste à régler : <b className="text-foreground">{manual.fee.remaining ?? manual.fee.amount} €</b>
            </p>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mode de paiement</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[{ k: "cash", l: "Espèces" }, { k: "bancontact", l: "Bancontact" }, { k: "lien", l: "Par lien" }, { k: "carte", l: "Carte" }].map((opt) => (
                <button type="button" key={opt.k} data-testid={`method-${opt.k}`}
                  onClick={() => setManualMethod(opt.k)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-bold transition-colors ${manualMethod === opt.k ? "bg-primary/15 border-primary/50 text-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
                  {opt.l}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Montant reçu (€)</label>
            <input data-testid="manual-amount-input" type="number" step="0.01" min="1" max={manual.fee.remaining ?? manual.fee.amount}
              value={manualAmount} onChange={(e) => setManualAmount(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
              placeholder={`Ex. ${manual.fee.remaining ?? manual.fee.amount}`} autoFocus />
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 mt-4">Référence (facultatif)</label>
            <input data-testid="manual-reference-input" value={manualRef} onChange={(e) => setManualRef(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
              placeholder="N° de virement, ticket, communication..." />
            <p className="text-xs text-muted-foreground mt-2">Le montant total atteint marque la cotisation « En ordre de paiement ».</p>
            <Button data-testid="manual-submit-btn" className="w-full mt-4" disabled={manualLoading}>
              <Wallet size={15} /> {manualLoading ? "Enregistrement..." : "Enregistrer le paiement"}
            </Button>
          </form>
        )}
      </Modal>

      <Modal open={!!editName} onClose={() => setEditName(null)}
        title="Modifier le nom de l'affilié">
        {editName && (
          <form onSubmit={saveName} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Prénom</label>
              <Input data-testid="edit-name-first" value={editName.first_name}
                onChange={(e) => setEditName({ ...editName, first_name: e.target.value })} autoFocus />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Nom</label>
              <Input data-testid="edit-name-last" value={editName.last_name}
                onChange={(e) => setEditName({ ...editName, last_name: e.target.value })} />
            </div>
            <Button data-testid="edit-name-submit" className="w-full" disabled={savingName}>
              <Pencil size={15} /> {savingName ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </form>
        )}
      </Modal>

      <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Réglages des cotisations">
        <form onSubmit={saveSettings}>
          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Montant par séance (€)</label>
          <input data-testid="session-fee-input" type="number" step="0.01" min="0.5"
            value={sessionFeeInput} onChange={(e) => setSessionFeeInput(e.target.value)}
            className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
            placeholder="Ex. 5" autoFocus />
          <p className="text-xs text-muted-foreground mt-2">Tarif appliqué par défaut aux joueurs « À la séance » lors de l'encaissement d'une présence.</p>
          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 mt-5">Lien de paiement en ligne (SumUp, etc.)</label>
          <input data-testid="payment-link-input" type="url"
            value={paymentLinkInput} onChange={(e) => setPaymentLinkInput(e.target.value)}
            className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
            placeholder="https://pay.sumup.com/b2c/XXXX" />
          <p className="text-xs text-muted-foreground mt-2">Lien externe ouvert par le bouton « Payer en ligne ». Laissez vide pour masquer le bouton.</p>
          <Button data-testid="save-settings-btn" className="w-full mt-4" disabled={savingSettings}>
            <Settings size={15} /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </form>
      </Modal>

      <Modal open={!!session} onClose={() => setSession(null)}
        title={session ? `Encaisser une séance — ${session.member.first_name} ${session.member.last_name}` : "Encaisser une séance"}>
        {session && (
          <form onSubmit={submitSession}>
            <p className="text-sm text-muted-foreground mb-4">
              Séances déjà payées : <b className="text-foreground">{session.member.session_count || 0}</b>
            </p>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mode de paiement</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[{ k: "lien", l: "Par lien" }, { k: "cash", l: "Espèces" }, { k: "carte", l: "Carte" }].map((opt) => (
                <button type="button" key={opt.k} data-testid={`session-method-${opt.k}`}
                  onClick={() => setSessionMethod(opt.k)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-bold transition-colors ${sessionMethod === opt.k ? "bg-primary/15 border-primary/50 text-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
                  {opt.l}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Montant reçu (€)</label>
            <input data-testid="session-amount-input" type="number" step="0.01" min="0.5"
              value={sessionAmount} onChange={(e) => setSessionAmount(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary"
              placeholder={`Ex. ${data.session_fee}`} autoFocus />
            <Button data-testid="session-submit-btn" className="w-full mt-4" disabled={sessionLoading}>
              <Wallet size={15} /> {sessionLoading ? "Enregistrement..." : "Encaisser la séance"}
            </Button>
          </form>
        )}
      </Modal>

      <Modal open={!!treasuryDetail} onClose={() => setTreasuryDetail(null)}
        title={treasuryDetail ? `Trésorerie — ${treasuryDetail.title}` : "Trésorerie"}>
        {treasuryDetail && (
          <div className="space-y-3" data-testid="treasury-detail">
            <div className="flex gap-2 flex-wrap text-sm">
              <Badge color="blue">Attendu : {treasuryDetail.expected} €</Badge>
              <Badge color="green">Encaissé : {treasuryDetail.collected} €</Badge>
              <Badge color="yellow">Reste : {treasuryDetail.remaining} €</Badge>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Restant à régler</p>
            {treasuryDetail.pending.length ? (
              <div className="space-y-2">
                {treasuryDetail.pending.map((p) => (
                  <div key={p.member_id} data-testid={`treasury-pending-${p.member_id}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                    <p className="text-sm font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">Payé {p.paid} € / {p.cost} € · <b className="text-yellow-500">reste {p.remaining} €</b></p>
                  </div>
                ))}
              </div>
            ) : <Empty text="Tous les enfants inscrits sont à jour ✓" />}
          </div>
        )}
      </Modal>

      <Modal open={!!addEventFor} onClose={() => setAddEventFor(null)}
        title={addEventFor ? `Ajouter un événement payant — ${addEventFor.first_name} ${addEventFor.last_name}` : "Ajouter un événement payant"}>
        {addEventFor && (
          (() => {
            const enrolled = new Set((addEventFor.events || []).map((e) => e.event_id));
            const available = paidEvents.filter((ev) => !enrolled.has(ev.id));
            return available.length ? (
              <div className="space-y-2" data-testid="add-event-list">
                {available.map((ev) => (
                  <button key={ev.id} data-testid={`add-event-option-${ev.id}`} onClick={() => enrollEvent(addEventFor, ev)}
                    className="w-full text-left rounded-lg border border-border bg-secondary/30 p-3 hover:border-primary transition-colors">
                    <p className="text-sm font-semibold">{ev.title}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(ev.date)} · {ev.cost} €{ev.deposit > 0 ? ` · acompte ${ev.deposit} €` : ""}</p>
                  </button>
                ))}
              </div>
            ) : <Empty text="Aucun événement payant disponible (créez un stage/événement avec un coût dans le Planning)." />;
          })()
        )}
      </Modal>
    </div>
  );
}


const euro = (v) => `${(Number(v) || 0).toFixed(2)} €`;

function ClubTreasury() {
  const [data, setData] = useState({ entries: [], summary: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const empty = { type: "recette", method: "banque", amount: "", label: "", category: "", date: new Date().toISOString().slice(0, 10) };
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fMethod, setFMethod] = useState("all");
  const [fCat, setFCat] = useState("all");

  const load = () => api.get("/club/treasury").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/club/ledger/${form.id}`, form);
      else await api.post("/club/ledger", form);
      toast.success("Enregistré"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette entrée ?")) return; await api.delete(`/club/ledger/${id}`); toast.success("Supprimé"); load(); };
  const markPaid = async (e) => { await api.put(`/club/ledger/${e.id}`, { ...e, status: null }); toast.success("Marqué comme réglé"); load(); };

  const allEntries = data.entries || [];
  const categories = [...new Set(allEntries.map((e) => e.category).filter(Boolean))].sort();
  const entries = allEntries.filter((e) => {
    const d = (e.date || "").slice(0, 10);
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    if (fMethod !== "all" && (e.method || "banque") !== fMethod) return false;
    if (fCat !== "all" && (e.category || "") !== fCat) return false;
    return true;
  });
  const hasFilter = fFrom || fTo || fMethod !== "all" || fCat !== "all";
  const s = (() => {
    const sum = (pred) => entries.filter(pred).reduce((a, e) => a + (Number(e.amount) || 0), 0);
    const meth = (e) => (e.method === "cash" ? "cash" : "banque");
    const isDep = (e) => e.type === "depense" && e.status !== "en_attente";
    const rb = sum((e) => e.type === "recette" && meth(e) === "banque");
    const rc = sum((e) => e.type === "recette" && meth(e) === "cash");
    const db_ = sum((e) => isDep(e) && meth(e) === "banque");
    const dc = sum((e) => isDep(e) && meth(e) === "cash");
    const pending = sum((e) => e.type === "depense" && e.status === "en_attente");
    const r2 = (x) => Math.round(x * 100) / 100;
    return { recettes_banque: r2(rb), recettes_cash: r2(rc), depenses_banque: r2(db_), depenses_cash: r2(dc),
      depenses_attente: r2(pending), solde_banque: r2(rb - db_), solde_cash: r2(rc - dc), solde_total: r2((rb - db_) + (rc - dc)) };
  })();
  const num = (v) => v || 0;
  const tiles = [
    { key: "recettes_banque", label: "Recettes Banque", value: num(s.recettes_banque), tone: "green" },
    { key: "recettes_cash", label: "Recettes Cash", value: num(s.recettes_cash), tone: "green" },
    { key: "depenses_banque", label: "Dépenses Banque", value: num(s.depenses_banque), tone: "red" },
    { key: "depenses_cash", label: "Dépenses Cash", value: num(s.depenses_cash), tone: "red" },
    { key: "solde_banque", label: "Solde Banque", value: num(s.solde_banque), tone: "solde" },
    { key: "solde_cash", label: "Solde Cash", value: num(s.solde_cash), tone: "solde" },
  ];
  if (num(s.depenses_attente) > 0) tiles.push({ key: "depenses_attente", label: "Dépenses en attente", value: num(s.depenses_attente), tone: "pending" });

  return (
    <div data-testid="club-treasury">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {tiles.map((t) => (
          <Card key={t.key} data-testid={`club-treasury-${t.key}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-widest font-bold">
              {t.tone === "green" && <TrendingUp size={13} className="text-green-500" />}
              {t.tone === "red" && <TrendingDown size={13} className="text-destructive" />}
              {t.tone === "solde" && <Wallet size={13} />}
              {t.tone === "pending" && <Clock size={13} className="text-yellow-500" />}
              {t.label}
            </div>
            <p className={`text-xl sm:text-2xl font-bold mt-2 ${t.tone === "green" ? "text-green-500" : t.tone === "red" ? "text-destructive" : t.tone === "pending" ? "text-yellow-500" : t.value >= 0 ? "text-primary" : "text-destructive"}`}>{euro(t.value)}</p>
          </Card>
        ))}
      </div>
      <Card data-testid="club-treasury-solde_total" className="border-primary/40 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold"><Wallet size={14} /> Solde Total (Banque + Cash)</div>
          <p className={`text-3xl font-black ${num(s.solde_total) >= 0 ? "text-primary" : "text-destructive"}`}>{euro(num(s.solde_total))}</p>
        </div>
      </Card>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-xl bg-secondary/40" data-testid="treasury-filters">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Du</label>
          <Input data-testid="treasury-filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Au</label>
          <Input data-testid="treasury-filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="w-40" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Mode</label>
          <Select data-testid="treasury-filter-method" value={fMethod} onChange={(e) => setFMethod(e.target.value)} className="w-32">
            <option value="all">Tous</option>
            <option value="banque">Banque</option>
            <option value="cash">Cash</option>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Catégorie</label>
          <Select data-testid="treasury-filter-category" value={fCat} onChange={(e) => setFCat(e.target.value)} className="w-44">
            <option value="all">Toutes</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        {hasFilter && (
          <button data-testid="treasury-filter-reset" onClick={() => { setFFrom(""); setFTo(""); setFMethod("all"); setFCat("all"); }}
            className="h-11 px-3 text-xs font-bold text-muted-foreground hover:text-foreground">Réinitialiser</button>
        )}
        <span className="ml-auto text-xs text-muted-foreground self-center">{entries.length} mouvement(s){hasFilter ? " (filtré)" : ""}</span>
      </div>

      <div className="flex justify-end gap-2 mb-4">
        <Button data-testid="export-club-treasury-pdf" variant="outline"
          onClick={() => downloadFile("/club/treasury.pdf", "tresorerie_club.pdf").catch(() => toast.error("Export échoué"))}>
          <FileText size={16} /> PDF
        </Button>
        <Button data-testid="add-club-ledger-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={16} /> Nouvelle entrée</Button>
      </div>

      {entries.length ? (
        <div className="space-y-2">
          {entries.map((e) => (
            <Card key={e.id} data-testid="club-ledger-row" className={`py-3 ${e.status === "en_attente" ? "border-yellow-500/40" : ""}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={e.type === "recette" ? "green" : "red"}>{e.type === "recette" ? "Recette" : "Dépense"}</Badge>
                    <Badge color={e.method === "cash" ? "yellow" : "blue"}>{e.method === "cash" ? "Cash" : "Banque"}</Badge>
                    {e.status === "en_attente" && <Badge color="yellow"><span className="flex items-center gap-1"><Clock size={11} /> En attente</span></Badge>}
                    {e.locked && <Badge color="gray"><span className="flex items-center gap-1"><Lock size={11} /> Cotisation</span></Badge>}
                    <span className="font-semibold text-sm break-words">{e.label || "—"}</span>
                    {e.category && !e.locked && <span className="text-xs text-muted-foreground">· {e.category}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDateUtil(e.date)}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`font-bold ${e.type === "recette" ? "text-green-500" : e.status === "en_attente" ? "text-yellow-500" : "text-destructive"}`}>{e.type === "recette" ? "+" : "−"}{euro(e.amount)}</span>
                  {!e.locked && (
                    <>
                      {e.status === "en_attente" && (
                        <button data-testid={`mark-paid-${e.id}`} onClick={() => markPaid(e)} title="Marquer comme réglé" className="text-muted-foreground hover:text-green-500"><CheckCircle2 size={16} /></button>
                      )}
                      <button data-testid={`edit-club-ledger-${e.id}`} onClick={() => { setForm({ ...e, method: e.method || "banque", date: (e.date || "").slice(0, 10) }); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                      <button data-testid={`delete-club-ledger-${e.id}`} onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucune entrée. Les cotisations payées apparaîtront ici automatiquement." />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Modifier l'entrée" : "Nouvelle entrée"}>
        {form && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Type</label>
                <Select data-testid="club-ledger-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="recette">Recette</option>
                  <option value="depense">Dépense</option>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mode de paiement</label>
                <Select data-testid="club-ledger-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                  <option value="banque">Banque</option>
                  <option value="cash">Cash</option>
                </Select>
              </div>
            </div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 mt-3">Libellé</label>
            <Input data-testid="club-ledger-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex. Subvention communale" required />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Montant (€)</label>
                <Input data-testid="club-ledger-amount" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Date</label>
                <Input data-testid="club-ledger-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 mt-3">Catégorie (optionnel)</label>
            <Input data-testid="club-ledger-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex. Subvention, Sponsoring, Matériel..." />
            <Button data-testid="save-club-ledger-btn" className="w-full mt-4">Enregistrer</Button>
          </form>
        )}
      </Modal>
    </div>
  );
}
